import { parseISO } from 'date-fns';
import type { Booking } from '@/types/Booking';

/**
 * Laying overlapping appointments out in a day column.
 *
 * Two appointments can share a slot for one of two reasons, and they want
 * opposite treatment:
 *
 *  - Both active. Only possible where double-booking is genuinely allowed —
 *    the agent's conflict check is scoped to `status = 'confirmed'`, so it
 *    never creates these itself. Both matter, so they go side by side.
 *  - One cancelled. Routine: cancelling frees the slot, so the same time is
 *    immediately rebookable. The cancelled one is history the active booking
 *    has superseded, and stacking them just obscures the live appointment.
 */

const isCancelled = (booking: Booking): boolean =>
  (booking.status ?? '').toLowerCase() === 'cancelled';

const startOf = (booking: Booking): number =>
  parseISO(booking.start_time).getTime();

const endOf = (booking: Booking): number =>
  parseISO(booking.end_time).getTime();

const overlaps = (a: Booking, b: Booking): boolean =>
  startOf(a) < endOf(b) && startOf(b) < endOf(a);

/**
 * Drops cancelled appointments that an active one has taken the slot of.
 * A cancelled appointment nothing has replaced is still returned — it stays
 * visible, greyed out, which is how a user sees that a slot opened up.
 */
export function hideSupersededCancellations(bookings: Booking[]): Booking[] {
  const active = bookings.filter(booking => !isCancelled(booking));
  if (active.length === 0) return bookings;

  return bookings.filter(
    booking =>
      !isCancelled(booking) || !active.some(other => overlaps(booking, other))
  );
}

export interface BookingColumn {
  /** 0-based position across the day column. */
  index: number;
  /** How many columns the overlapping group needs. */
  count: number;
}

/**
 * Assigns each appointment a column so overlapping ones sit beside each other.
 *
 * Appointments are swept in start order and collected into groups that overlap
 * transitively — A with B and B with C puts all three in one group, so they
 * share a width even when A and C do not touch. Within a group each booking
 * takes the first column already free at its start time.
 *
 * @returns Column assignment per booking id. Bookings with no overlap get
 *   `{ index: 0, count: 1 }`, i.e. the full width, as before.
 */
export function computeOverlapColumns(
  bookings: Booking[]
): Map<number, BookingColumn> {
  const layout = new Map<number, BookingColumn>();
  const ordered = [...bookings].sort((a, b) => startOf(a) - startOf(b));

  let group: Booking[] = [];
  let groupEnd = -Infinity;

  const flush = () => {
    if (group.length === 0) return;

    // Column index -> the end time of the last booking placed in it.
    const columnEnds: number[] = [];
    const assigned: Array<{ booking: Booking; index: number }> = [];

    group.forEach(booking => {
      let index = columnEnds.findIndex(end => end <= startOf(booking));
      if (index === -1) {
        index = columnEnds.length;
      }
      columnEnds[index] = endOf(booking);
      assigned.push({ booking, index });
    });

    assigned.forEach(({ booking, index }) => {
      layout.set(booking.id, { index, count: columnEnds.length });
    });

    group = [];
    groupEnd = -Infinity;
  };

  ordered.forEach(booking => {
    if (group.length > 0 && startOf(booking) >= groupEnd) {
      flush();
    }
    group.push(booking);
    groupEnd = Math.max(groupEnd, endOf(booking));
  });
  flush();

  return layout;
}
